import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import qahoy from "views_shared/app/javascript/scripts/qahoy";
import qapi from "views_shared/app/javascript/api";
import ImageCropped from "views_shared/app/javascript/components/ImageCropped";
import { companyUrl } from "views_shared/app/javascript/utils/urls";
import SectionCard from "../../../profiles/SectionCard";
import SidebarSkeletonList from "./sidebar_skeleton_list";
import { CompanyContext } from "../../data/company_context_management";

function PrAgencyList({ previewMode = false }) {
  const { companyId, company, viewerContext, setPrAgenciesExist, setOpenEditModal } = useContext(CompanyContext);
  const editable = !!viewerContext?.editable;
  const [isLoading, setIsLoading] = useState(true);
  const [prAgencies, setPrAgencies] = useState([]);
  const [asyncError, setAsyncError] = useState();

  // Retrieve the pr_profiles list from the API
  useEffect(() => {
    let componentIsMounted = true;
    const fetchPrAgencies = async () => {
      try {
        if (companyId) {
          setIsLoading(true);
          qahoy.track("Companies#getPrAgencies");

          const response = await qapi.companyRepresentations.getAllByPromotedCompanyId(companyId);
          if (componentIsMounted && response.data) {
            const prAgencyResults = response.data
              .map((item) => ({
                id: item.promoter_profile_id,
                company_id: item.company_id,
                company_name: item.company_name,
                company_profile_image_url: item.company_profile_image_url,
                company_slug: item.company_slug,
                company_migrate_id: item.company_migrate_id,
              }))
              .reduce((unique, item) => {
                return unique.find((e) => e.id === item.id) ? unique : [...unique, item];
              }, []);

            if (prAgencyResults) {
              if (previewMode) {
                // Limit to 3 records if we're in preview mode
                setPrAgencies(prAgencyResults.slice(0, 3));
              } else {
                setPrAgencies(prAgencyResults);
              }
            }
          }
          if (componentIsMounted) setIsLoading(false);
        }
      } catch (error) {
        setAsyncError(error);
      }
    };
    fetchPrAgencies();

    // Cleanup function to avoid the "Can't perform state update on unmounted component" error
    return () => {
      componentIsMounted = false;
    };
  }, [companyId, previewMode]);

  // Update company context to indicate PR agency records exist
  useEffect(() => {
    setPrAgenciesExist(prAgencies.length > 0);
  }, [prAgencies, setPrAgenciesExist]);

  // Let top-level ErrorBoundary see any async errors
  if (asyncError) throw asyncError;

  if (!isLoading && prAgencies.length === 0) return null;
  if (!company.page_config?.show_pr_agencies && !previewMode) return null;

  return (
    <SectionCard
      title="Our PR Agencies"
      editable={!previewMode && editable}
      onEdit={!previewMode ? () => setOpenEditModal("PR_AGENCIES") : null}
      editButtonId={!previewMode ? "modal-button-PR_AGENCIES" : undefined}
    >
      {isLoading ? (
        <SidebarSkeletonList numberOfLines={2} />
      ) : (
        prAgencies.map((agency) => (
          <div className="d-flex ms-1 mb-2 align-items-center animated faster fadeIn" key={agency.company_id}>
            {agency.company_profile_image_url && (
              <ImageCropped
                className="rounded"
                wrapperClasses="me-1"
                src={agency.company_profile_image_url}
                alt={agency.company_name}
                size={45}
                fallbackToInitials
              />
            )}
            <div className="flex-grow-1">
              <a
                href={companyUrl({ slug: agency.company_slug, migrate_id: agency.company_migrate_id })}
                style={{ fontWeight: "bold" }}
              >
                {agency.company_name}
              </a>
            </div>
          </div>
        ))
      )}
    </SectionCard>
  );
}

export default PrAgencyList;

PrAgencyList.propTypes = {
  previewMode: PropTypes.bool,
};
